import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Info from "@insite/mobius/Icons/Info";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { shippingAddressStyles } from "@tKgpco/Overrides/Widgets/CheckoutShipping/ShippingAddress";
import packingListNotesAndInstructions from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/PackingListNotesAndInstructionHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    packingListNotesAndInstructions,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SpecialDeliveryInstruction {
    container?: GridContainerProps;
    instructionGridItem?: GridItemProps;
    textHeader?: TypographyPresentationProps;
    textNote?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    CheckboxGroupWapper?: InjectableCss;
    checkbox?: CheckboxProps;
    CheckboxGroup?: CheckboxGroupPresentationProps;
    shipTooltip?: TooltipPresentationProps;
}

export const specialDeliveryInstruction: SpecialDeliveryInstruction = {
    wrapper: {
        css: css`
            width: 100%;
            > div {
                vertical-align: bottom;
            }
        `,
    },
    textHeader: {
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            padding-right: 5px;
        `,
    },
    instructionGridItem: {
        width: [12, 6, 4, 4, 4],
        css: css`
            padding: 10px 15px;
            padding-bottom: 0;
        `,
    },
    CheckboxGroup: {
        css: css`
            flex-direction: row;
            margin: 10px 0;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    shipTooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
        },
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: ${COLORS.primary.base};
                    width: 18px;
                    height: 18px;
                }
            `,
        },
    },
};

const styles = specialDeliveryInstruction;
const PackingListNotesAndInstructions = ({ cart, packingListNotesAndInstructions }: Props) => {
    const notesAndInstructions: any[] = [
        {
            label: siteMessage("BlindShip"),
            key: "BlindShip",
            isChecked: false,
        },
        {
            label: siteMessage("ShipResidential"),
            key: "ShipResidential",
            isChecked: false,
        },
        {
            label: siteMessage("ShipComplete"),
            key: "ShipComplete",
            isChecked: false,
        },
    ];
    const packingAndInstructionModifyArr: { label: string; isChecked: boolean | undefined; key: string }[] = [];
    const selectedNotesAndInstructionsArry: string[] | undefined = cart?.properties?.packingListAndInstructions?.split(
        "|",
    );
    const [listAndNotesSelectedArray, setListAndNotesSelectedArray] = useState<string[]>([]);

    // eslint-disable-next-line array-callback-return
    notesAndInstructions.map((v, i) => {
        const selectedCheckbox = selectedNotesAndInstructionsArry?.includes(v.key);
        if (selectedCheckbox) {
            packingAndInstructionModifyArr.push({
                label: v.label,
                isChecked: selectedCheckbox,
                key: v.key,
            });
        } else {
            packingAndInstructionModifyArr.push({
                label: v.label,
                isChecked: selectedCheckbox || false,
                key: v.key,
            });
        }
    });
    useEffect(() => {
        packingListNotesAndInstructions({
            listAndNotesSelectedArray,
        });
    }, [listAndNotesSelectedArray]);
    let duplicateArray: string[];
    useEffect(() => {
        if (cart?.isAwaitingApproval && selectedNotesAndInstructionsArry) {
            duplicateArray = selectedNotesAndInstructionsArry;
            setListAndNotesSelectedArray(duplicateArray);
        }
    }, []);
    const onChangeHandler = (e: any, key: any) => {
        const selectedArray: string[] = [...listAndNotesSelectedArray];
        if (e.target.getAttribute("aria-checked") === "false") {
            selectedArray.push(key);
        } else {
            selectedArray.splice(selectedArray.indexOf(key), 1);
        }
        setListAndNotesSelectedArray(selectedArray);
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography as="span" {...styles.textHeader}>
                {translate("Packing List Notes and instructions (printed on packing list only)")}
            </Typography>
            <Tooltip
                {...shippingAddressStyles.shipTooltip}
                className="toolTip-wrapper"
                text={siteMessage("PackagingListNotes_Info") as string}
            />
            <GridContainer {...styles.container}>
                {packingAndInstructionModifyArr?.map((value, index) => (
                    <GridItem {...styles.instructionGridItem} key={value.label}>
                        <CheckboxGroup
                            {...styles.CheckboxGroup}
                            onClick={e => onChangeHandler(e, value.key)}
                            name={value.label}
                            defaultValue={value.label}
                        >
                            <Checkbox
                                {...styles.checkbox}
                                checked={value.isChecked}
                                key={value.label}
                                data-test-selector="orderReview-instructions"
                            >
                                {value.label}
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PackingListNotesAndInstructions),
    definition: {
        displayName: "Packing List Notes And Instructions",
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
